import { memo, useState } from 'react';
import DocumentTypeSelect from './DocumentTypeSelect';
import InsuranceCompanySelect from './InsuranceCompanySelect';
import ProductTypeSelect from './ProductTypeSelect';
import ProductNameKeyword from './ProductNameKeyword';
import DateRangePicker from '../../DateRangePicker';
import SidebarIcon from './SidebarIcon';
import { INSURANCE_TYPE, getInsuranceTypeName } from '../../../utils/insuranceTypeUtils';
import { isFeatureEnabledProductNameSearch } from '../../../utils/featureFlags';

/**
 * 搭載文書検索の検索条件サイドバーパネル
 *
 * 生損保タブ・文書種別・保険会社・商品種類・加入時期・文書名キーワードの
 * 各検索条件入力コンポーネントをまとめたサイドバー。
 * PC では最小化（アイコンのみ表示）にも対応する。
 *
 * @param {object} props
 * @param {number} props.insuranceType - 生損保区分（INSURANCE_TYPE.LIFE | INSURANCE_TYPE.SONPO）
 * @param {number|null} props.selectedDocumentTypeId - 選択中の文書種別ID
 * @param {number[]} props.selectedCompanyIds - 選択中の保険会社IDリスト
 * @param {string[]} props.selectedCategories - 選択中のカテゴリリスト
 * @param {string} props.dateFrom - 加入時期開始（YYYY/MM/DD）
 * @param {string} props.dateTo - 加入時期終了（YYYY/MM/DD）
 * @param {string} props.keyword - 文書名キーワード
 * @param {Array} props.insuranceCompanies - 保険会社マスター一覧
 * @param {Array} props.documentTypesAccess - アクセス可能な文書種別一覧
 * @param {Function} props.onInsuranceTypeChange - 生損保区分変更コールバック
 * @param {Function} props.onDocumentTypeChange - 文書種別変更コールバック
 * @param {Function} props.onCompanyChange - 保険会社変更コールバック
 * @param {Function} props.onCategoryChange - カテゴリ変更コールバック
 * @param {Function} props.onDateFromChange - 加入時期開始変更コールバック
 * @param {Function} props.onDateToChange - 加入時期終了変更コールバック
 * @param {Function} props.onKeywordChange - キーワード変更コールバック
 * @param {Function} props.onSearch - 検索実行コールバック
 * @param {Function} props.onReset - 条件リセットコールバック
 * @param {boolean} props.isLoading - 検索実行中フラグ（検索・リセットボタンを無効化）
 * @param {boolean} props.sidebarMinimized - PC でサイドバーが最小化状態かどうか
 * @param {Function} props.onToggleSidebar - サイドバー開閉トグルコールバック
 * @param {boolean} [props.isMobile=false] - モバイル表示かどうか
 * @param {boolean} [props.sidebarActive=false] - モバイルでサイドバーが開いているかどうか
 */
const SearchPanel = ({
  insuranceType,
  selectedDocumentTypeId,
  selectedCompanyIds,
  selectedCategories,
  dateFrom,
  dateTo,
  keyword,
  insuranceCompanies,
  documentTypesAccess,
  onInsuranceTypeChange,
  onDocumentTypeChange,
  onCompanyChange,
  onCategoryChange,
  onDateFromChange,
  onDateToChange,
  onKeywordChange,
  onSearch,
  onReset,
  isLoading,
  sidebarMinimized,
  onToggleSidebar,
  isMobile = false,
  sidebarActive = false,
}) => {
  const [hasError, setHasError] = useState(false);

  if (!isMobile && sidebarMinimized) {
    return (
      <aside className="insurance-sidebar minimized">
        <div className="minimized-sidebar-content">
          <button className="sidebar-toggle-button-pc select-box ver03" onClick={onToggleSidebar}>
            <SidebarIcon />
            <span>開く</span>
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`insurance-sidebar${isMobile ? ` mobile${sidebarActive ? ' active' : ''}` : ''}`}>
      {!isMobile && (
        <div className="sidebar-toggle-pc">
          <button className="sidebar-toggle-button-pc select-box ver02" onClick={onToggleSidebar}>
            <SidebarIcon />
            <span>閉じる</span>
          </button>
        </div>
      )}

      <div className="sidebar-header">搭載文書 検索条件</div>
      <div className="sidebar-section">
        <div className="document-section">

          {/* 生損保区分 - タブ（タイトルなし） */}
          <div className="dc-tabs-section">
            <div className="dc-tabs">
              <button
                className={`dc-tab-btn${insuranceType === INSURANCE_TYPE.LIFE ? ' dc-tab-btn--active' : ''}`}
                onClick={() => onInsuranceTypeChange(INSURANCE_TYPE.LIFE)}
              >
                {getInsuranceTypeName(INSURANCE_TYPE.LIFE)}
              </button>
              <button
                className={`dc-tab-btn${insuranceType === INSURANCE_TYPE.SONPO ? ' dc-tab-btn--active' : ''}`}
                onClick={() => onInsuranceTypeChange(INSURANCE_TYPE.SONPO)}
              >
                {getInsuranceTypeName(INSURANCE_TYPE.SONPO)}
              </button>
            </div>
          </div>

          {/* 文書種別 - ドロップダウン（ラベルなし） */}
          <div className="dc-field-block">
            <DocumentTypeSelect
              insuranceType={insuranceType}
              selectedId={selectedDocumentTypeId}
              onChange={onDocumentTypeChange}
              documentTypesAccess={documentTypesAccess}
            />
          </div>

          {/* 保険会社 - ポップアップ（タイトル自己描画） */}
          <InsuranceCompanySelect
            insuranceType={insuranceType}
            insuranceCompanies={insuranceCompanies}
            selectedIds={selectedCompanyIds}
            onChange={onCompanyChange}
          />

          {/* 保険商品種類 - ポップアップ（タイトル自己描画） */}
          <ProductTypeSelect
            insuranceType={insuranceType}
            selectedValues={selectedCategories}
            onChange={onCategoryChange}
          />

          {/* 加入時期 */}
          <DateRangePicker
            dateFrom={dateFrom}
            setDateFrom={onDateFromChange}
            dateTo={dateTo}
            setDateTo={onDateToChange}
            onHasError={setHasError}
          />

          {/* 商品名検索 */}
          {isFeatureEnabledProductNameSearch() && (
            <div className="word-search">
              <div className="dc-field-block-label dc-field-label-flex">商品名検索</div>
              <ProductNameKeyword value={keyword} onChange={onKeywordChange} />
            </div>
          )}

          <div className="button-container">
            <button
              className="search-btn"
              onClick={onSearch}
              disabled={isLoading || hasError}
            >
              検索
            </button>
            <button
              className="reset-btn"
              onClick={onReset}
              disabled={isLoading}
            >
              条件をリセットする
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default memo(SearchPanel);
